import {
    getNationBuilderBrief,
    getNationBuilderBriefVariables,
} from '__generated__/getNationBuilderBrief';
import { NationBuilderBriefFields } from '__generated__/NationBuilderBriefFields';
import { Currency } from 'common/utils/currency';
import DesignerCardContext from 'projects/components/DesignerCardContext';
import DesignerMetaContext from 'projects/components/DesignerMetaContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { BriefStep, StepState } from 'projects/util/step';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './NationBuilder.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

interface Props {
    briefId: string;
    step: string;
}

const steps: BriefStep<NationBuilderBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: [
            'language',
            'title',
            'needs',
            'appType',
            'mobileAppType',
            'existingWebsite',
            'description',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
        ],
    },
    {
        step: 2,
        title: __('Your time & budget'),
        fields: ['startDate', 'endDate', 'timeframe', 'budget'],
    },
    {
        step: 3,
        title: __('About you'),
        fields: ['politicalGroup', 'organizationType', 'profileApproval', 'other'],
    },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export function NationBuilder() {
    const route = useRouteMatch<Props>();

    return (
        <BriefQuery<getNationBuilderBrief, getNationBuilderBriefVariables>
            query={queries.getNationBuilderBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="NationBuilderBriefFields"
        >
            {(data, refetch) => {
                const { viewer } = data;
                const brief = {
                    ...data.brief,
                    fields: data.brief.fields as NationBuilderBriefFields,
                };

                const step = new StepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                });

                const siteCurrency = data.site.currency as Currency;

                return (
                    <Brief
                        refetch={refetch}
                        schema="nationBuilder"
                        brief={brief}
                        viewer={viewer}
                        stepState={step}
                    >
                        {form => (
                            <Switch>
                                <Route
                                    path={`/briefs/nationbuilder/:briefId/1`}
                                    render={() => <DetailsPage form={form} brief={brief} />}
                                />
                                <Route
                                    path={`/briefs/nationbuilder/:briefId/2`}
                                    render={() => (
                                        <TimeAndBudgetPage
                                            form={form}
                                            brief={brief}
                                            siteCurrency={siteCurrency}
                                        />
                                    )}
                                />
                                <Route
                                    path={`/briefs/nationbuilder/:briefId/3`}
                                    render={() => <AboutYouPage form={form} brief={brief} />}
                                />
                                <Route
                                    path={`/briefs/nationbuilder/:briefId/4`}
                                    render={() => <SummaryPage brief={brief} />}
                                />
                                <Route
                                    path={`/briefs/nationbuilder/:briefId/5`}
                                    render={() => (
                                        <DesignerMetaContext.Provider
                                            value={{
                                                hasMoreResultsUrl: false,
                                            }}
                                        >
                                            <DesignerCardContext.Provider
                                                value={{
                                                    highlightNewDesigners: false,
                                                    stats: [],
                                                    selectDesignerLabels: {
                                                        selected: __('Added'),
                                                        notSelected: __('Add'),
                                                    },
                                                }}
                                            >
                                                <InviteDesigners
                                                    brief={brief}
                                                    viewer={data.viewer}
                                                    description={__(
                                                        'We’ve matched you with these awesome designers. Invite up to 5 to quote'
                                                    )}
                                                />
                                            </DesignerCardContext.Provider>
                                        </DesignerMetaContext.Provider>
                                    )}
                                />
                            </Switch>
                        )}
                    </Brief>
                );
            }}
        </BriefQuery>
    );
}
